import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';
import { Plus, Edit2, Trash2, Calendar, LayoutDashboard, Users, FileSpreadsheet, Link, RefreshCw, X, ChevronDown, ChevronRight, ChevronsDown, ChevronsUp, Square, CheckSquare, ArrowRightLeft } from 'lucide-react';
import DatePicker from '../../components/ui/DatePicker';
import NumberInput from '../../components/ui/NumberInput';
import Select from '../../components/ui/Select';
import ConfirmModal from '../../components/ui/ConfirmModal';
import MoveToOrgModal from '../../components/ui/MoveToOrgModal';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

export default function GanttAdminPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { currentOrg, currentOrgId, organizations } = useOrganization();

    // Gantt permission helpers
    const canManageGantt = user?.is_superadmin || user?.can_manage_gantt || user?.role === 'gantt_user';
    const canViewGantt   = canManageGantt || user?.can_view_gantt;
    const canCreateGantt = canManageGantt || user?.can_create_gantt;
    const canEditGantt   = canManageGantt || user?.can_edit_gantt;
    const canDeleteGantt = canManageGantt || user?.can_delete_gantt;
    // Tashkilotga ko'chirish — Super Admin yoki can_manage_organizations
    const canMoveGantt = organizations.length > 1 && !!(user?.is_superadmin || user?.can_manage_organizations);
    const [moveModal, setMoveModal] = useState({ open: false, items: [] });

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    const PROJECT_COLORS = [
        '', '#ef4444', '#f97316', '#f59e0b', '#eab308',
        '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4',
        '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
        '#d946ef', '#ec4899', '#f43f5e', '#64748b', '#78716c'
    ];

    // Modal state
    const [projectModal, setProjectModal] = useState({ open: false, data: null });
    const [sectionModal, setSectionModal] = useState({ open: false, data: null, projectId: null });
    const [taskModal, setTaskModal] = useState({ open: false, data: null, projectId: null, sectionId: null, currentProgress: 0, currentStatus: 'todo', selectedDeps: [], notes: '' });

    const openTaskModal = (data, projectId, sectionId) => {
        const rawDeps = data?.dependency_ids || data?.dependencies || [];
        const selectedDeps = (Array.isArray(rawDeps) ? rawDeps : (typeof rawDeps === 'string' ? rawDeps.split(',').map(s => s.trim()) : []))
            .map(String)
            .filter(Boolean);

        setTaskModal({
            open: true,
            data,
            projectId,
            sectionId,
            currentProgress: data?.progress || 0,
            currentStatus: data?.status || 'todo',
            selectedDeps,
            notes: data?.notes || ''
        });
    };

    const closeTaskModal = () => {
        setTaskModal({ open: false, data: null, projectId: null, sectionId: null, currentProgress: 0, currentStatus: 'todo', selectedDeps: [], notes: '' });
    };
    const [usersModal, setUsersModal] = useState({ open: false, projectId: null, projectName: '', createdById: null, selectedUsers: [], allUsers: [] });
    const [deleteModal, setDeleteModal] = useState({ open: false, type: null, id: null, title: '' });
    const [selectedColor, setSelectedColor] = useState(PROJECT_COLORS[10]);
    const [gsheetModal, setGsheetModal] = useState({ open: false, projectId: null, sheetUrl: '', projectName: '', syncing: false, error: null });

    // Collapse / expand state — default: all collapsed
    const [expandedProjects, setExpandedProjects] = useState(new Set());
    const [expandedSections, setExpandedSections] = useState(new Set());

    const toggleProject = (projectId) => {
        setExpandedProjects(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    };

    const expandAllSections = (project) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            (project.sections || []).forEach(s => next.add(s.id));
            return next;
        });
    };

    const collapseAllSections = (project) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            (project.sections || []).forEach(s => next.delete(s.id));
            return next;
        });
    };

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId);
            else next.add(sectionId);
            return next;
        });
    };

    useEffect(() => {
        fetchProjects();
    }, [currentOrgId]);

    // To'liq "Loading..." faqat birinchi marta — keyingi qayta yuklashlarda
    // (bo'lim almashtirish, saqlashdan keyin) eski ro'yxat ko'rinib turadi.
    const hasLoadedRef = useRef(false);

    const fetchProjects = async () => {
        try {
            if (!hasLoadedRef.current) setLoading(true);
            const orgParam = currentOrgId ? { organization_id: currentOrgId } : {};
            const { data } = await api.get('/gantt/projects?admin=true', { params: orgParam });
            // Fetch tasks and sections for each project
            const projectsWithTasks = await Promise.all(
                data.map(async (p) => {
                    const [taskRes, sectionRes] = await Promise.all([
                        api.get(`/gantt/projects/${p.id}/tasks`),
                        api.get(`/gantt/projects/${p.id}/sections`)
                    ]);
                    return { ...p, tasks: taskRes.data, sections: sectionRes.data };
                })
            );
            setProjects(projectsWithTasks);
        } catch (error) {
            console.error(error);
        } finally {
            hasLoadedRef.current = true;
            setLoading(false);
        }
    };

    const handleSaveProject = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const payload = Object.fromEntries(formData);
        
        payload.auto_sync = formData.get('auto_sync') === 'on';
        payload.sync_interval_days = formData.get('sync_interval_days') ? parseInt(formData.get('sync_interval_days'), 10) : null;
        payload.sync_time = formData.get('sync_time') || null;

        if (!projectModal.data && currentOrgId) {
            payload.organization_id = currentOrgId;
        }

        try {
            if (projectModal.data) {
                await api.put(`/gantt/projects/${projectModal.data.id}`, payload);
            } else {
                await api.post('/gantt/projects', payload);
            }
            setProjectModal({ open: false, data: null });
            fetchProjects();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteProject = (id, name) => {
        setDeleteModal({ open: true, type: 'project', id, title: t('gantt.delete_project_confirm', `Удалить проект "${name}"?`, { name }) });
    };

    const handleSaveTask = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const payload = Object.fromEntries(formData);
        payload.progress = parseInt(payload.progress, 10);
        payload.dependency_ids = taskModal.selectedDeps;
        payload.notes = taskModal.notes || null;
        
        try {
            if (taskModal.data) {
                await api.put(`/gantt/tasks/${taskModal.data.id}`, payload);
            } else {
                payload.project_id = taskModal.projectId;
                await api.post('/gantt/tasks', payload);
            }
            closeTaskModal();
            fetchProjects();
        } catch (err) {
            alert(err.message || 'Error saving task');
        }
    };

    const handleSyncGSheet = async (e) => {
        e.preventDefault();
        setGsheetModal(prev => ({ ...prev, syncing: true, error: null, success: null }));
        try {
            const formData = new FormData(e.target);
            const payload = {
                sheet_url: gsheetModal.sheetUrl,
                project_name: gsheetModal.projectName || null,
                project_id: gsheetModal.projectId || null,
                auto_sync: formData.get('auto_sync') === 'on',
                sync_interval_days: formData.get('sync_interval_days') ? parseInt(formData.get('sync_interval_days'), 10) : null,
                sync_time: formData.get('sync_time') || null
            };
            const { data: result } = await api.post('/gantt/projects/sync-gsheet', payload);
            setGsheetModal(prev => ({ 
                ...prev, 
                syncing: false, 
                success: `✅ ${result.project_name || t('gantt.sync_success_msg')}: ${result.tasks_count || 0} ${t('gantt.sync_tasks_word')}, ${result.sections_count || 0} ${t('gantt.sync_sections_word')}` 
            }));
            fetchProjects();
        } catch (err) {
            setGsheetModal(prev => ({ ...prev, syncing: false, error: `${t('gantt.sync_error')}: ${err.message}`, success: null }));
        }
    };

    const handleSaveSection = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const payload = Object.fromEntries(formData);
        
        try {
            if (sectionModal.data) {
                await api.put(`/gantt/sections/${sectionModal.data.id}`, payload);
            } else {
                payload.project_id = sectionModal.projectId;
                await api.post('/gantt/sections', payload);
            }
            setSectionModal({ open: false, data: null, projectId: null });
            fetchProjects();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteSection = (id, name) => {
        setDeleteModal({ open: true, type: 'section', id, title: t('gantt.delete_section_confirm', `Удалить раздел "${name}"?`, { name }) });
    };

    const handleDeleteTask = (id, name) => {
        setDeleteModal({ open: true, type: 'task', id, title: t('gantt.delete_task_confirm', `Удалить задачу "${name}"?`, { name }) });
    };

    const executeDelete = async () => {
        try {
            if (deleteModal.type === 'project') {
                await api.delete(`/gantt/projects/${deleteModal.id}`);
            } else if (deleteModal.type === 'section') {
                await api.delete(`/gantt/sections/${deleteModal.id}`);
            } else if (deleteModal.type === 'task') {
                await api.delete(`/gantt/tasks/${deleteModal.id}`);
            }
            setDeleteModal({ open: false, type: null, id: null, title: '' });
            fetchProjects();
        } catch (err) {
            console.error(err);
        }
    };

    const openUsersModal = async (projectId, projectName, createdById) => {
        try {
            const [usersRes, projectUsersRes] = await Promise.all([
                api.get('/admin/users'),
                api.get(`/gantt/projects/${projectId}/users`)
            ]);
            const existingUserIds = new Set(projectUsersRes.data.user_ids || []);
            const allUsers = usersRes.data;

            // Build selected user objects
            const selectedUsers = allUsers
                .filter(u => existingUserIds.has(u.id))
                .map(u => ({
                    userId: u.id,
                    name: [u.last_name_ru || u.last_name_uz, u.first_name_ru || u.first_name_uz].filter(Boolean).join(' ') || u.login,
                    login: u.login,
                    role: u.role,
                }));

            setUsersModal({ open: true, projectId, projectName: projectName || '', createdById: createdById || null, selectedUsers, allUsers });
        } catch (e) {
            console.error(e);
        }
    };

    const addUserToGantt = (val) => {
        if (!val) return;
        const u = usersModal.allUsers.find(u => String(u.id) === String(val));
        if (!u || usersModal.selectedUsers.find(s => String(s.userId) === String(u.id))) return;
        const name = [u.last_name_ru || u.last_name_uz, u.first_name_ru || u.first_name_uz].filter(Boolean).join(' ') || u.login;
        setUsersModal(prev => ({
            ...prev,
            selectedUsers: [...prev.selectedUsers, { userId: u.id, name, login: u.login, role: u.role }]
        }));
    };

    const removeUserFromGantt = (userId) => {
        setUsersModal(prev => ({
            ...prev,
            selectedUsers: prev.selectedUsers.filter(s => String(s.userId) !== String(userId))
        }));
    };

    const handleSaveUsers = async () => {
        try {
            await api.post(`/gantt/projects/${usersModal.projectId}/users`, {
                user_ids: usersModal.selectedUsers.map(s => s.userId)
            });
            setUsersModal({ open: false, projectId: null, projectName: '', createdById: null, selectedUsers: [], allUsers: [] });
            fetchProjects();
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">{t('gantt.admin_title', 'Gantt Chart Management')}</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setGsheetModal({ open: true, projectId: null, sheetUrl: '', projectName: '', autoSync: false, syncIntervalDays: 3, syncTime: '07:00', syncing: false, error: null })}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition"
                    >
                        <FileSpreadsheet className="w-4 h-4" /> {t('gantt.sync_gsheet', 'Google Sheets')}
                    </button>
                    {canCreateGantt && (
                    <button
                        onClick={() => { setProjectModal({ open: true, data: null }); setSelectedColor(PROJECT_COLORS[10]); }}
                        className="flex items-center gap-2 px-4 py-2 bg-brand text-inverse rounded-lg font-semibold hover:bg-brand/90 transition"
                    >
                        <Plus className="w-4 h-4" /> {t('gantt.new_project', 'Новый Проект')}
                    </button>
                    )}
                </div>
            </div>

            <div className="space-y-4">
                {projects.map(project => {
                    const isProjectExpanded = expandedProjects.has(project.id);
                    const allSectionIds = (project.sections || []).map(s => s.id);
                    const anyExpanded = allSectionIds.some(id => expandedSections.has(id));
                    const allExpanded = allSectionIds.length > 0 && allSectionIds.every(id => expandedSections.has(id));
                    const uncatTasks = project.tasks?.filter(t => !t.section_id) || [];

                    return (
                    <div key={project.id} className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
                        {/* ── Project header ── */}
                        <div
                            className="flex items-center justify-between p-4 cursor-pointer select-none hover:bg-surface-2/30 transition-colors"
                            onClick={() => toggleProject(project.id)}
                        >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <ChevronDown
                                    className={`w-4 h-4 text-muted flex-shrink-0 transition-transform duration-200 ${isProjectExpanded ? '' : '-rotate-90'}`}
                                />
                                <LayoutDashboard className="w-5 h-5 text-brand flex-shrink-0" />
                                <h2 className="text-lg font-bold text-primary truncate">{project.name}</h2>
                                <div className="text-sm text-muted hidden sm:flex items-center gap-3 ml-2">
                                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(project.start_date)} &mdash; {formatDate(project.end_date)}</span>
                                    <span className="px-2 py-0.5 rounded-full bg-surface-2 border border-border text-xs uppercase">{t(`gantt.project_status_${project.status}`, project.status)}</span>
                                    {project.creator_name && (
                                        <span className="text-xs text-brand font-medium border-l border-border pl-3">
                                            {t('gantt.creator')}: {project.creator_name}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                {/* Expand/Collapse All sections — only when project is open */}
                                {isProjectExpanded && allSectionIds.length > 0 && (
                                    <>
                                        <button
                                            onClick={() => expandAllSections(project)}
                                            disabled={allExpanded}
                                            className="p-1.5 text-muted hover:text-brand rounded-md disabled:opacity-30 transition"
                                            title={t('gantt.expand_all', 'Barchasini yoy')}
                                        >
                                            <ChevronsDown className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => collapseAllSections(project)}
                                            disabled={!anyExpanded}
                                            className="p-1.5 text-muted hover:text-brand rounded-md disabled:opacity-30 transition"
                                            title={t('gantt.collapse_all', 'Barchasini yig\'')}
                                        >
                                            <ChevronsUp className="w-4 h-4" />
                                        </button>
                                    </>
                                )}
                                <button onClick={() => setGsheetModal({ open: true, projectId: project.id, sheetUrl: project.sheet_url || '', projectName: project.name, autoSync: project.auto_sync || false, syncIntervalDays: project.sync_interval_days || 3, syncTime: project.sync_time || '07:00', syncing: false, error: null })} className="p-2 text-muted hover:text-emerald-500 hover:bg-emerald-500/10 rounded-lg" title={t('gantt.sync_gsheet', 'Google Sheets Sync')}>
                                    <RefreshCw className="w-4 h-4" />
                                </button>
                                {canManageGantt && (
                                    <button onClick={() => openUsersModal(project.id, project.name, project.created_by_id)} className="p-2 text-muted hover:text-brand hover:bg-brand/10 rounded-lg" title={t('gantt.permissions', 'Ruxsatlar')}>
                                        <Users className="w-4 h-4" />
                                    </button>
                                )}
                                {canMoveGantt && (
                                    <button onClick={() => setMoveModal({ open: true, items: [{ id: project.id, title: project.name }] })} className="p-2 text-muted hover:text-brand hover:bg-brand/10 rounded-lg" title={t('organizations.move_title', "Tashkilotga ko'chirish")}>
                                        <ArrowRightLeft className="w-4 h-4" />
                                    </button>
                                )}
                                {canEditGantt && (
                                    <button onClick={() => { setProjectModal({ open: true, data: project }); setSelectedColor(project.color || PROJECT_COLORS[10]); }} className="p-2 text-muted hover:text-brand hover:bg-brand/10 rounded-lg">
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                )}
                                {canDeleteGantt && (
                                    <button onClick={() => handleDeleteProject(project.id, project.name)} className="p-2 text-muted hover:text-error hover:bg-error/10 rounded-lg">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ── Project content (sections + tasks) ── */}
                        <AnimatePresence initial={false}>
                            {isProjectExpanded && (
                                <motion.div
                                    key="project-content"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <div className="px-4 pb-4 space-y-3 border-t border-border/40">

                                        {/* Uncategorized tasks */}
                                        {uncatTasks.length > 0 && (
                                            <div className="space-y-2 pt-3">
                                                {uncatTasks.map(task => (
                                                    <div key={task.id} className="flex items-center justify-between p-3 rounded-lg bg-surface-2/50 border border-border hover:border-brand/30 transition">
                                                        <div className="flex-1">
                                                            <div className="font-semibold text-sm">{task.name} <span className="text-muted font-normal text-xs ml-2">({task.progress}%)</span></div>
                                                            <div className="text-xs text-muted mt-1">{formatDate(task.start_date)} &mdash; {formatDate(task.end_date)} | {t('gantt.assignee', 'Исполнитель')}: {task.assignee || t('gantt.unassigned', 'Не назначен')}</div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button onClick={() => openTaskModal(task, project.id, null)} className="p-1.5 text-muted hover:text-brand rounded-md"><Edit2 className="w-3.5 h-3.5" /></button>
                                                            <button onClick={() => handleDeleteTask(task.id, task.name)} className="p-1.5 text-muted hover:text-error rounded-md"><Trash2 className="w-3.5 h-3.5" /></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Sections */}
                                        {project.sections?.map(section => {
                                            const isSectionExpanded = expandedSections.has(section.id);
                                            const sectionTasks = project.tasks?.filter(t => t.section_id === section.id) || [];
                                            return (
                                                <div key={section.id} className="bg-surface-2/30 rounded-lg border border-border">
                                                    {/* Section header — clickable */}
                                                    <div
                                                        className="flex items-center justify-between px-3 py-2.5 cursor-pointer select-none hover:bg-surface-2/60 rounded-lg transition-colors"
                                                        onClick={() => toggleSection(section.id)}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <ChevronRight
                                                                className={`w-4 h-4 text-muted flex-shrink-0 transition-transform duration-200 ${isSectionExpanded ? 'rotate-90' : ''}`}
                                                            />
                                                            <h3 className="font-bold text-primary">{section.name}</h3>
                                                            <span className="text-xs text-muted ml-1">({sectionTasks.length})</span>
                                                        </div>
                                                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                            {canCreateGantt && (
                                                                <button onClick={() => { openTaskModal(null, project.id, section.id); setExpandedSections(prev => new Set(prev).add(section.id)); }} className="p-1.5 text-muted hover:text-brand rounded-md" title={t('gantt.add_task', 'Добавить задачу')}>
                                                                    <Plus className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                            {canEditGantt && (
                                                                <button onClick={() => setSectionModal({ open: true, data: section, projectId: project.id })} className="p-1.5 text-muted hover:text-brand rounded-md">
                                                                    <Edit2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                            {canDeleteGantt && (
                                                                <button onClick={() => handleDeleteSection(section.id, section.name)} className="p-1.5 text-muted hover:text-error rounded-md">
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Section tasks — animated */}
                                                    <AnimatePresence initial={false}>
                                                        {isSectionExpanded && (
                                                            <motion.div
                                                                key="section-tasks"
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.18, ease: 'easeInOut' }}
                                                                style={{ overflow: 'hidden' }}
                                                            >
                                                                <div className="space-y-2 px-3 pb-3 pt-1">
                                                                    {sectionTasks.map(task => (
                                                                        <div key={task.id} className="flex items-center justify-between p-3 rounded-lg bg-surface border border-border hover:border-brand/30 transition shadow-sm">
                                                                            <div className="flex-1">
                                                                                <div className="font-semibold text-sm">{task.name} <span className="text-muted font-normal text-xs ml-2">({task.progress}%)</span></div>
                                                                                <div className="text-xs text-muted mt-1">{formatDate(task.start_date)} &mdash; {formatDate(task.end_date)} | {t('gantt.assignee', 'Исполнитель')}: {task.assignee || t('gantt.unassigned', 'Не назначен')}</div>
                                                                            </div>
                                                                            <div className="flex items-center gap-2">
                                                                                {canEditGantt && (
                                                                                    <button onClick={() => openTaskModal(task, project.id, section.id)} className="p-1.5 text-muted hover:text-brand rounded-md"><Edit2 className="w-3.5 h-3.5" /></button>
                                                                                )}
                                                                                {canDeleteGantt && (
                                                                                    <button onClick={() => handleDeleteTask(task.id, task.name)} className="p-1.5 text-muted hover:text-error rounded-md"><Trash2 className="w-3.5 h-3.5" /></button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                    {sectionTasks.length === 0 && <div className="text-xs text-muted italic pb-1">{t('gantt.no_tasks_in_section', 'Нет задач в этом разделе')}</div>}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            );
                                        })}

                                        {/* Add section / task buttons */}
                                        <div className="flex items-center gap-4 pt-1">
                                            {canCreateGantt && (
                                                <button onClick={() => setSectionModal({ open: true, data: null, projectId: project.id })} className="text-sm font-semibold text-brand hover:underline flex items-center gap-1">
                                                    <Plus className="w-3.5 h-3.5" /> {t('gantt.add_section', 'Добавить раздел')}
                                                </button>
                                            )}
                                            {canCreateGantt && (
                                                <button onClick={() => openTaskModal(null, project.id, null)} className="text-sm font-semibold text-muted hover:text-brand hover:underline flex items-center gap-1">
                                                    <Plus className="w-3.5 h-3.5" /> {t('gantt.add_task', 'Добавить общую задачу')}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    );
                })}
            </div>

            {/* Modals */}
            {projectModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleSaveProject} className="bg-surface w-full max-w-md rounded-2xl shadow-xl border border-border flex flex-col">
                        <div className="p-5 border-b border-border">
                            <h3 className="text-lg font-bold">{projectModal.data ? t('gantt.edit_project', 'Редактировать Проект') : t('gantt.new_project', 'Новый Проект')}</h3>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('subcategories.name', 'Название')}</label>
                                <input name="name" defaultValue={projectModal.data?.name} required className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.start_date', 'Начало')}</label>
                                    <DatePicker name="start_date" defaultValue={projectModal.data?.start_date} required className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.end_date', 'Конец')}</label>
                                    <DatePicker name="end_date" defaultValue={projectModal.data?.end_date} required className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.status', 'Статус')}</label>
                                <Select 
                                    name="status" 
                                    defaultValue={projectModal.data?.status || 'active'} 
                                    options={[
                                        { value: 'active', label: t('gantt.project_status_active') },
                                        { value: 'completed', label: t('gantt.project_status_completed') },
                                        { value: 'delayed', label: t('gantt.project_status_delayed') }
                                    ]}
                                />
                            </div>
                            {user?.role === 'admin' ? (
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-2">{t('gantt.project_color', 'Цвет проекта')}</label>
                                    <input type="hidden" name="color" value={selectedColor || ''} />
                                    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                                        {PROJECT_COLORS.map(c => (
                                            <button
                                                key={c || 'none'}
                                                type="button"
                                                onClick={() => setSelectedColor(c)}
                                                className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all relative overflow-hidden ${selectedColor === c ? 'border-primary scale-110 shadow-md' : 'border-transparent hover:scale-110'} ${!c ? 'bg-surface-2 border-border/50' : ''}`}
                                                style={c ? { backgroundColor: c } : {}}
                                                title={!c ? t('gantt.no_color', 'Без цвета') : undefined}
                                            >
                                                {!c && <div className="absolute inset-0 w-full h-[2px] bg-red-500/50 m-auto -rotate-45" />}
                                                {selectedColor === c && c && <div className="w-2 h-2 bg-white rounded-full mix-blend-difference" />}
                                                {selectedColor === c && !c && <div className="w-2 h-2 bg-primary rounded-full z-10" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <input type="hidden" name="color" value={selectedColor || ''} />
                            )}
                            {/* Google Sheet URL & Auto Sync (visible if they import or edit) */}
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1 flex items-center gap-1">
                                    <FileSpreadsheet className="w-3 h-3 text-emerald-500" />
                                    {t('gantt.sheet_url', 'Google Sheet URL')}
                                </label>
                                <div className="flex gap-2">
                                    <input 
                                        name="sheet_url"
                                        value={projectModal.sheetUrl ?? projectModal.data?.sheet_url ?? ''} 
                                        onChange={(e) => setProjectModal(prev => ({ ...prev, sheetUrl: e.target.value }))}
                                        placeholder="https://docs.google.com/spreadsheets/d/..."
                                        className="flex-1 bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm"
                                    />
                                    {projectModal.data && (
                                        <button 
                                            type="button"
                                            onClick={() => {
                                                const url = projectModal.sheetUrl ?? projectModal.data?.sheet_url;
                                                if (!url) return;
                                                setProjectModal({ open: false, data: null });
                                                setGsheetModal({ open: true, projectId: projectModal.data.id, sheetUrl: url, projectName: projectModal.data.name, autoSync: projectModal.data?.auto_sync || false, syncIntervalDays: projectModal.data?.sync_interval_days || 3, syncTime: projectModal.data?.sync_time || '07:00', syncing: false, error: null, success: null });
                                            }}
                                            disabled={!(projectModal.sheetUrl ?? projectModal.data?.sheet_url)}
                                            className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5 flex-shrink-0"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                            {t('gantt.resync', 'Re-sync')}
                                        </button>
                                    )}
                                </div>
                                
                                <div className="mt-4 p-3 bg-surface-2/50 border border-border rounded-lg">
                                    <label className="flex items-center gap-2 cursor-pointer mb-3">
                                        <input 
                                            type="checkbox" 
                                            name="auto_sync" 
                                            defaultChecked={projectModal.data?.auto_sync || false}
                                            className="hidden"
                                            onChange={(e) => {
                                                setProjectModal(prev => ({...prev, _force: !prev._force}));
                                            }}
                                            id="auto_sync_check"
                                        />
                                        <div className={`transition-colors ${(document.getElementById('auto_sync_check')?.checked ?? projectModal.data?.auto_sync) ? 'text-brand' : 'text-muted'}`}>
                                            {(document.getElementById('auto_sync_check')?.checked ?? projectModal.data?.auto_sync) ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                        </div>
                                        <span className="text-sm font-semibold">{t('gantt.auto_sync', 'Avtomatik yangilash (Auto Sync)')}</span>
                                    </label>
                                    
                                    <div id="auto_sync_fields" className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-bold text-muted uppercase mb-1">{t('gantt.sync_interval', 'Necha kunda? (Kun)')}</label>
                                            <input 
                                                type="number" 
                                                name="sync_interval_days" 
                                                defaultValue={projectModal.data?.sync_interval_days || 3}
                                                min="1"
                                                className="w-full bg-surface border border-border rounded-lg px-3 py-1.5 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-muted uppercase mb-1">{t('gantt.sync_time', 'Qaysi vaqtda? (HH:MM)')}</label>
                                            <input 
                                                type="time" 
                                                name="sync_time" 
                                                defaultValue={projectModal.data?.sync_time || '07:00'}
                                                className="w-full bg-surface border border-border rounded-lg px-3 py-1.5 text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2 bg-surface-2/30">
                            <button type="button" onClick={() => setProjectModal({ open: false, data: null })} className="px-4 py-2 rounded-lg font-semibold text-secondary hover:bg-surface-2">{t('users.cancel', 'Отмена')}</button>
                            <button type="submit" className="px-4 py-2 bg-brand text-inverse rounded-lg font-semibold">{t('users.save', 'Сохранить')}</button>
                        </div>
                    </form>
                </div>
            )}

            {sectionModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleSaveSection} className="bg-surface w-full max-w-sm rounded-2xl shadow-xl border border-border flex flex-col">
                        <div className="p-5 border-b border-border">
                            <h3 className="text-lg font-bold">{sectionModal.data ? t('gantt.edit_section', 'Редактировать Раздел') : t('gantt.add_section', 'Добавить раздел')}</h3>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('subcategories.name', 'Название')}</label>
                                <input name="name" defaultValue={sectionModal.data?.name} required className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2 bg-surface-2/30">
                            <button type="button" onClick={() => setSectionModal({ open: false, data: null, projectId: null })} className="px-4 py-2 rounded-lg font-semibold text-secondary hover:bg-surface-2">{t('users.cancel', 'Отмена')}</button>
                            <button type="submit" className="px-4 py-2 bg-brand text-inverse rounded-lg font-semibold">{t('users.save', 'Сохранить')}</button>
                        </div>
                    </form>
                </div>
            )}

            {taskModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleSaveTask} className="bg-surface w-full max-w-md rounded-2xl shadow-xl border border-border flex flex-col">
                        <div className="p-5 border-b border-border">
                            <h3 className="text-lg font-bold">{taskModal.data ? t('gantt.edit_task', 'Редактировать Задачу') : t('gantt.new_task', 'Новая Задача')}</h3>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.task_name', 'Название задачи')}</label>
                                <input name="name" defaultValue={taskModal.data?.name} required className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.assignee', 'Исполнитель')}</label>
                                <input name="assignee" defaultValue={taskModal.data?.assignee} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.start_date', 'Начало')}</label>
                                    <DatePicker name="start_date" defaultValue={taskModal.data?.start_date} required className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.end_date', 'Конец')}</label>
                                    <DatePicker name="end_date" defaultValue={taskModal.data?.end_date} required className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.section', 'Раздел')}</label>
                                <Select 
                                    name="section_id" 
                                    defaultValue={taskModal.data?.section_id || taskModal.sectionId || ''} 
                                    options={[
                                        { value: '', label: t('gantt.no_section', 'Без раздела') },
                                        ...(projects.find(p => p.id === taskModal.projectId)?.sections?.map(s => ({ value: s.id, label: s.name })) || [])
                                    ]}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.progress', 'Прогресс (%)')}</label>
                                    <NumberInput 
                                        name="progress" 
                                        min={0} 
                                        max={100} 
                                        value={taskModal.currentProgress} 
                                        onChange={(val) => {
                                            let newStatus = taskModal.currentStatus;
                                            if (val === 100) newStatus = 'done';
                                            else if (val > 0 && val < 100) newStatus = 'in_progress';
                                            else if (val === 0) newStatus = 'todo';

                                            setTaskModal(prev => ({
                                                ...prev,
                                                currentProgress: val,
                                                currentStatus: newStatus
                                            }));
                                        }}
                                        required 
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.status', 'Статус')}</label>
                                    <Select 
                                        name="status" 
                                        value={taskModal.currentStatus} 
                                        onChange={(val) => setTaskModal(prev => ({ ...prev, currentStatus: val }))}
                                        options={[
                                            { value: 'todo', label: t('gantt.status_todo') },
                                            { value: 'in_progress', label: t('gantt.status_in_progress') },
                                            { value: 'done', label: t('gantt.status_done') }
                                        ]}
                                    />
                                </div>
                            </div>
                            {/* Dependencies multi-select */}
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.dependencies', 'Зависимости')}</label>
                                <p className="text-[11px] text-muted mb-2">{t('gantt.dep_hint', 'Эта задача не может быть завершена, пока выбранные задачи не будут выполнены')}</p>
                                <div className="max-h-48 overflow-y-auto border border-border rounded-lg bg-surface-2">
                                    {(() => {
                                        const currentProject = projects.find(p => p.id === taskModal.projectId);
                                        if (!currentProject) return null;
                                        const allTasks = (currentProject.tasks || []).filter(t => t.id !== taskModal.data?.id);
                                        const sections = currentProject.sections || [];
                                        const uncatTasks = allTasks.filter(t => !t.section_id);
                                        
                                        const renderTaskItem = (t) => {
                                            const isSelected = taskModal.selectedDeps.map(String).includes(String(t.id));
                                            return (
                                                <div
                                                    key={t.id}
                                                    onClick={() => {
                                                        setTaskModal(prev => {
                                                            const strId = String(t.id);
                                                            const currentDeps = prev.selectedDeps.map(String);
                                                            const isSel = currentDeps.includes(strId);
                                                            return {
                                                                ...prev,
                                                                selectedDeps: isSel 
                                                                    ? currentDeps.filter(id => id !== strId)
                                                                    : [...currentDeps, strId]
                                                            };
                                                        });
                                                    }}
                                                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-surface transition text-sm ${isSelected ? 'bg-brand/5' : ''}`}
                                                >
                                                    <span className={`flex-shrink-0 transition-colors ${isSelected ? 'text-brand' : 'text-muted'}`}>
                                                        {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                    </span>
                                                    <span className={`flex-1 truncate ${isSelected ? 'font-semibold text-brand' : 'text-primary'}`}>{t.name}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${t.status === 'done' ? 'bg-green-500/10 text-green-600' : t.status === 'in_progress' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-surface text-muted'}`}>
                                                        {t.status === 'done' ? '✓' : t.status === 'in_progress' ? '◐' : '○'}
                                                    </span>
                                                </div>
                                            );
                                        };

                                        return (
                                            <>
                                                {uncatTasks.map(renderTaskItem)}
                                                {sections.map(section => {
                                                    const sectionTasks = allTasks.filter(t => t.section_id === section.id);
                                                    if (sectionTasks.length === 0) return null;
                                                    return (
                                                        <div key={section.id}>
                                                            <div className="px-3 py-1.5 bg-surface-2 border-t border-b border-border/30">
                                                                <span className="text-[10px] font-bold text-muted uppercase tracking-wider">{section.name}</span>
                                                            </div>
                                                            {sectionTasks.map(renderTaskItem)}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        );
                                    })()}
                                </div>
                                {taskModal.selectedDeps.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {taskModal.selectedDeps.map(depId => {
                                            const depTask = projects.find(p => String(p.id) === String(taskModal.projectId))?.tasks?.find(t => String(t.id) === String(depId));
                                            return depTask ? (
                                                <span key={depId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand/10 text-brand text-xs rounded-full font-medium">
                                                    {depTask.name}
                                                    <X className="w-3 h-3 cursor-pointer hover:text-error" onClick={() => setTaskModal(prev => ({ ...prev, selectedDeps: prev.selectedDeps.map(String).filter(id => id !== String(depId)) }))} />
                                                </span>
                                            ) : null;
                                        })}
                                    </div>
                                )}
                            </div>
                            {/* Notes */}
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.notes', 'Заметки')}</label>
                                <textarea
                                    value={taskModal.notes}
                                    onChange={(e) => setTaskModal(prev => ({ ...prev, notes: e.target.value }))}
                                    rows={3}
                                    placeholder={t('gantt.notes_placeholder', 'Краткий комментарий о текущем состоянии...')}
                                    className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm resize-none"
                                />
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2 bg-surface-2/30">
                            <button type="button" onClick={closeTaskModal} className="px-4 py-2 rounded-lg font-semibold text-secondary hover:bg-surface-2">{t('users.cancel', 'Отмена')}</button>
                            <button type="submit" className="px-4 py-2 bg-brand text-inverse rounded-lg font-semibold">{t('users.save', 'Сохранить')}</button>
                        </div>
                    </form>
                </div>
            )}

            <AnimatePresence>
                {usersModal.open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={e => { if (e.target === e.currentTarget) setUsersModal({ open: false, projectId: null, projectName: '', selectedUsers: [], allUsers: [] }); }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between p-5 border-b border-border rounded-t-2xl">
                                <div>
                                    <h2 className="text-base font-bold text-primary flex items-center gap-2">
                                        <Users className="w-5 h-5 text-brand" />
                                        {t('gantt.grant_permission', 'Foydalanuvchilarga kirish')}
                                    </h2>
                                    {usersModal.projectName && (
                                        <p className="text-xs text-muted mt-0.5 uppercase tracking-wider">{usersModal.projectName}</p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setUsersModal({ open: false, projectId: null, projectName: '', selectedUsers: [], allUsers: [] })}
                                    className="p-1.5 rounded-lg hover:bg-surface-2 text-muted hover:text-primary transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-5 space-y-4">
                                {/* Search dropdown */}
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-muted uppercase tracking-wider">
                                        {t('docs.add_user', 'Foydalanuvchi qo\'shish')}
                                    </label>
                                    <Select
                                        searchable
                                        placeholder={t('docs.select_user_placeholder', 'Foydalanuvchini tanlang...')}
                                        value=''
                                        options={[
                                            { value: '', label: t('docs.select_user_placeholder', 'Foydalanuvchini tanlang...') },
                                            ...usersModal.allUsers
                                                .filter(u => !usersModal.selectedUsers.find(s => String(s.userId) === String(u.id)))
                                                .map(u => ({
                                                    value: String(u.id),
                                                    label: [u.last_name_ru || u.last_name_uz, u.first_name_ru || u.first_name_uz].filter(Boolean).join(' ') || u.login
                                                }))
                                        ]}
                                        onChange={addUserToGantt}
                                    />
                                </div>

                                {/* Selected users list */}
                                {usersModal.selectedUsers.length > 0 && (
                                    <div className="space-y-1">
                                        <div className="px-1 pb-1">
                                            <span className="text-xs font-bold text-muted uppercase tracking-wider">
                                                {t('docs.assigned_users', 'Tayinlangan foydalanuvchilar')}
                                            </span>
                                        </div>
                                        <div className="space-y-1 max-h-56 overflow-y-auto">
                                            {usersModal.selectedUsers.map(u => (
                                                <div
                                                    key={u.userId}
                                                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-surface-2 border border-border"
                                                >
                                                    {/* Avatar */}
                                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 bg-brand/20 text-brand">
                                                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                                                    </div>
                                                    {/* Name + login */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <p className="text-sm font-semibold text-primary truncate">{u.name}</p>
                                                            {usersModal.createdById && String(u.userId) === String(usersModal.createdById) && (
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-brand/15 text-brand border border-brand/20 flex-shrink-0">АВТОР</span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-muted">{u.login} ({u.role})</p>
                                                    </div>
                                                    {/* Remove button — disabled for creator */}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeUserFromGantt(u.userId)}
                                                        disabled={usersModal.createdById && String(u.userId) === String(usersModal.createdById)}
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-border text-muted transition-colors flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:text-red-500 hover:enabled:border-red-400/40 hover:enabled:bg-red-500/5"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between px-5 py-4 border-t border-border bg-surface-2 rounded-b-2xl">
                                <span className="text-xs text-muted">
                                    {usersModal.selectedUsers.length} {t('docs.viewers_count', "foydalanuvchi")}
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setUsersModal({ open: false, projectId: null, projectName: '', selectedUsers: [], allUsers: [] })}
                                        className="px-4 py-2 rounded-xl border border-border text-sm font-semibold text-primary hover:bg-surface transition-colors"
                                    >
                                        {t('users.cancel', 'Bekor qilish')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveUsers}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                                    >
                                        {t('users.save', 'Saqlash')}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            {/* Google Sheets Sync Modal */}
            {gsheetModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form key={gsheetModal.projectId || 'new'} onSubmit={handleSyncGSheet} className="bg-surface w-full max-w-md rounded-2xl shadow-xl border border-border flex flex-col">
                        <div className="p-5 border-b border-border">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <FileSpreadsheet className="w-5 h-5 text-emerald-500" />
                                {gsheetModal.projectId ? t('gantt.resync_gsheet', 'Re-sync from Google Sheets') : t('gantt.sync_gsheet', 'Import from Google Sheets')}
                            </h3>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-muted uppercase mb-1">
                                    <Link className="w-3 h-3 inline mr-1" />{t('gantt.sheet_url', 'Google Sheet URL')}
                                </label>
                                <input
                                    value={gsheetModal.sheetUrl}
                                    onChange={(e) => setGsheetModal(prev => ({ ...prev, sheetUrl: e.target.value }))}
                                    required
                                    placeholder="https://docs.google.com/spreadsheets/d/..."
                                    className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm"
                                />
                            </div>
                            {!gsheetModal.projectId && (
                                <div>
                                    <label className="block text-xs font-bold text-muted uppercase mb-1">{t('gantt.project_name', 'Название проекта')}</label>
                                    <input
                                        value={gsheetModal.projectName}
                                        onChange={(e) => setGsheetModal(prev => ({ ...prev, projectName: e.target.value }))}
                                        placeholder={t('gantt.project_name_placeholder', 'Оставьте пустым для авто')}
                                        className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm"
                                    />
                                </div>
                            )}
                            
                            <div className="mt-4 p-3 bg-surface-2/50 border border-border rounded-lg">
                                <label className="flex items-center gap-2 cursor-pointer mb-3">
                                    <input 
                                        type="checkbox" 
                                        name="auto_sync" 
                                        defaultChecked={gsheetModal.autoSync || false}
                                        className="hidden"
                                        onChange={(e) => {
                                            setGsheetModal(prev => ({...prev, _force: !prev._force}));
                                        }}
                                        id="gsheet_auto_sync_check"
                                    />
                                    <div className={`transition-colors ${(document.getElementById('gsheet_auto_sync_check')?.checked ?? gsheetModal.autoSync) ? 'text-emerald-500' : 'text-muted'}`}>
                                        {(document.getElementById('gsheet_auto_sync_check')?.checked ?? gsheetModal.autoSync) ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                    </div>
                                    <span className="text-sm font-semibold">{t('gantt.auto_sync', 'Avtomatik yangilash (Auto Sync)')}</span>
                                </label>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-muted uppercase mb-1">{t('gantt.sync_interval', 'Necha kunda? (Kun)')}</label>
                                        <input 
                                            type="number" 
                                            name="sync_interval_days" 
                                            defaultValue={gsheetModal.syncIntervalDays || 3}
                                            min="1"
                                            className="w-full bg-surface border border-border rounded-lg px-3 py-1.5 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-muted uppercase mb-1">{t('gantt.sync_time', 'Qaysi vaqtda? (HH:MM)')}</label>
                                        <input 
                                            type="time" 
                                            name="sync_time" 
                                            defaultValue={gsheetModal.syncTime || '07:00'}
                                            className="w-full bg-surface border border-border rounded-lg px-3 py-1.5 text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            {gsheetModal.error && (
                                <div className="p-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error font-medium">{gsheetModal.error}</div>
                            )}
                            {gsheetModal.success && (
                                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-emerald-600 dark:text-emerald-400 font-medium">{gsheetModal.success}</div>
                            )}
                            {gsheetModal.projectId && !gsheetModal.success && (
                                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-600 dark:text-amber-400">
                                    ⚠️ {t('gantt.resync_warning', 'Resync will replace all existing tasks and sections in this project!')}
                                </div>
                            )}
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2 bg-surface-2/30">
                            <button type="button" onClick={() => setGsheetModal({ open: false, projectId: null, sheetUrl: '', projectName: '', syncing: false, error: null, success: null })} className="px-4 py-2 rounded-lg font-semibold text-secondary hover:bg-surface-2">
                                {gsheetModal.success ? t('gantt.close', 'Закрыть') : t('users.cancel', 'Отмена')}
                            </button>
                            {!gsheetModal.success && (
                                <button type="submit" disabled={gsheetModal.syncing} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                                    {gsheetModal.syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                                    {gsheetModal.syncing ? t('gantt.syncing', 'Синхронизация...') : t('gantt.sync_btn', 'Синхронизировать')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            )}

            <MoveToOrgModal
                open={moveModal.open}
                onClose={() => setMoveModal({ open: false, items: [] })}
                itemType="gantt_projects"
                items={moveModal.items}
                onMoved={() => fetchProjects()}
            />

            <ConfirmModal
                isOpen={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, type: null, id: null, title: '' })}
                onConfirm={executeDelete}
                title={t('gantt.delete_confirm_title', 'Подтверждение удаления')}
                message={deleteModal.title}
                confirmText={t('gantt.delete', 'Удалить')}
                cancelText={t('users.cancel', 'Отмена')}
            />
        </div>
    );
}
